import { useState, useEffect } from 'react';
import api from '../api';

// Module-level cache so we only call /permissions/my-sections once per session
let cachedSections = null;
let cachePromise = null;

/**
 * Returns whether the current user has access to a given route path,
 * based on the Permissions module (role defaults + custom grants by admin).
 *
 * @param {string} path - e.g. "/documents-consolidated"
 * @returns {boolean | null} true / false / null (still loading)
 */
export const useSectionAccess = (path) => {
  const [hasAccess, setHasAccess] = useState(cachedSections ? cachedSections.includes(path) : null);

  useEffect(() => {
    let active = true;

    const resolve = async () => {
      if (cachedSections) {
        if (active) setHasAccess(cachedSections.includes(path));
        return;
      }
      if (!cachePromise) {
        cachePromise = api
          .get('/permissions/my-sections')
          .then((r) => r.data?.sections || [])
          .catch(() => []);
      }
      try {
        cachedSections = await cachePromise;
      } catch {
        cachedSections = [];
      }
      if (active) setHasAccess(cachedSections.includes(path));
    };

    resolve();
    return () => {
      active = false;
    };
  }, [path]);

  return hasAccess;
};

// Allow callers to bust the cache (e.g. after a user logs out / role changes)
export const resetSectionAccessCache = () => {
  cachedSections = null;
  cachePromise = null;
};
